import { useMediaQuery } from "@mui/material";
import { useIsClient } from "@uidotdev/usehooks";
import { isEmpty } from "lodash";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "react-toastify/dist/ReactToastify.css";
import { debounce } from "../../../helpers/debounce";
import {
  getPostApplyRecommendedJobs,
  updateJobListFilter,
  updateSelectedJob,
} from "../../../redux/actions/job_action";
import SingleJobCompanyProfile from "../SingleJobWrapper/SingleJobCompanyProfile/SingleJobCompanyProfile";
import SingleJobDescription from "../SingleJobWrapper/SingleJobDescription/SingleJobDescription";
import SingleJobHighlightWithLogo from "../SingleJobWrapper/SingleJobHighlight/SingleJobHighlightWithLogo";
import SingleJobMedia from "../SingleJobWrapper/SingleJobMedia/SingleJobMedia";
import ExpiredJobBanner from "./ExpiredJobBanner/ExpiredJobBanner";
import ExpiredJobTitle from "./ExpiredJobTitle/ExpiredJobTitle";
import ExpiredSimilarJobs from "./ExpiredSimilarJobs/ExpiredSimilarJobs";
import {
  BoxContainer,
  DividerStyled,
  JobDetailContainer,
  JobDetailWrapper,
  JobHighlightContainer,
  ViewLessIcon,
  ViewMoreButton,
  ViewMoreIcon,
  Wrapper,
} from "./styles";

export default function ExpiredJobWrapper(props) {
  const { job, similarJobs } = props;

  const isClient = useIsClient();
  const dispatch = useDispatch();

  // State to toggle the visibility of detailed job information
  const [showJobDetails, setShowJobDetails] = useState(false);

  // Dynamic text and icon for the view more/less button
  const viewText = showJobDetails
    ? "Hide full job details"
    : "View full job details";

  const viewIcon = showJobDetails ? <ViewLessIcon /> : <ViewMoreIcon />;

  // Handler for toggling job details visibility
  function handleViewJobDetails() {
    setShowJobDetails(!showJobDetails);
  }

  // Fetch recommended jobs on component mount
  useEffect(() => {
    const params = {
      globalId: job?.globalId,
      first: 9,
      internship: job?.jobType === "Internship",
    };

    dispatch(updateSelectedJob(job));
    dispatch(getPostApplyRecommendedJobs(params));
  }, [dispatch]);

  return (
    <>
      <ExpiredJobBanner />
      <Wrapper>
        <ExpiredJobTitle job={job} />
        <ExpiredSimilarJobs similarJobs={similarJobs} />

        <DividerStyled sx={{ margin: "0 0 2rem" }} />

        <SingleJobMedia job={job} />
        <SingleJobCompanyProfile job={job} />

        {showJobDetails ? <JobDetails job={job} /> : null}

        {isClient ? (
          <>
            <BoxContainer justifyContent="center" margin="2rem 0 1.5rem">
              <ViewMoreButton onClick={handleViewJobDetails}>
                {viewText} {viewIcon}
              </ViewMoreButton>
            </BoxContainer>
            <DividerStyled sx={{ margin: "0 0 2rem" }} />
          </>
        ) : null}
      </Wrapper>
    </>
  );
}

function JobDetails(props) {
  const { job } = props;

  const dispatch = useDispatch();

  const isDesktop = useMediaQuery("(min-width: 1280px)");

  const stateRegionsList = useSelector((state) => state?.jobs?.stateRegions);
  const jobTypesList = useSelector((state) => state?.jobs?.jobTypes);

  function handleHref(type, job) {
    //to prevent the browser spamming dispatch when user return to previous page
    //to clear the title, if not the search bar is not empty
    const debouncedDispatch = debounce((dispatch) => {
      if (type !== "title") dispatch(updateJobListFilter({ keyword: "" }));
    }, 500);

    debouncedDispatch(dispatch);

    switch (type) {
      case "jobTitle":
        const jobTitle = job?.title ?? job?.aggregatedCompanyName;
        const processedJobTitle = jobTitle
          .replaceAll(/[(){}[\]"`~,./&%$*!@-]/g, "")
          .trim()
          .replaceAll(" ", "-");

        return `/${encodeURIComponent(
          processedJobTitle
        )}-jobs?filterOrigin=jdp-text`;

      case "stateRegion":
        const jobState = stateRegionsList.filter(
          (state) => state?.state == job?.stateRegion
        );
        const jobStateSlug = !isEmpty(jobState)
          ? jobState[0]?.state?.replace(" ", "+")
          : "Overseas";

        return `/jobs?state=${jobStateSlug}&filterOrigin=jdp-text`;

      case "jobType":
        const jobType = job?.jobType
          ? job?.jobType
          : job?.job_type
            ? job?.job_type
            : null;

        const selectedJobType = jobTypesList?.filter(
          (currentJobType) =>
            currentJobType?.title?.toLowerCase() == jobType?.toLowerCase()
        );

        return `/jobs?job-type=${selectedJobType[0]?.id}&filterOrigin=jdp-text`;

      case "specialisation":
        let specialisationSlug = "";
        const jobTracks = job?.tracks;

        jobTracks?.forEach((track) => {
          specialisationSlug += "specialisation=" + track?.id + "&";
        });

        return `/jobs?${specialisationSlug.slice(
          0,
          specialisationSlug.length - 1
        )}&filterOrigin=jdp-text`;
      case "experience":
        let experienceURL = "/jobs?";
        const yearsExperience = {
          min: job?.minYearsExperience,
          max: job?.maxYearsExperience > 11 ? 11 : job?.maxYearsExperience,
        };
        const experienceList = [
          [-1],
          [0],
          [1, 2, 3],
          [4, 5, 6, 7],
          [8, 9, 10],
          [11],
        ];

        const minRange = experienceList.find((value) =>
          value.includes(yearsExperience?.min)
        );
        const indexInExpListMin = experienceList.indexOf(minRange) + 1;

        const maxRange = experienceList.find((value) =>
          value.includes(yearsExperience?.max)
        );
        const indexInExpListMax = experienceList.indexOf(maxRange) + 1;

        if (
          indexInExpListMin == indexInExpListMax &&
          job?.jobType != "Internship"
        ) {
          experienceURL += `experience=${indexInExpListMin}&`;
        } else if (
          indexInExpListMin == indexInExpListMax &&
          job?.jobType == "Internship"
        ) {
          experienceURL += `experience=${1}&`;
        } else {
          for (let i = 0; i < experienceList.length; i++) {
            if (i + 1 >= indexInExpListMin && i + 1 <= indexInExpListMax) {
              experienceURL += `experience=${i + 1}&`;
            }
          }
        }
        return `${experienceURL.slice(
          0,
          experienceURL.length - 1
        )}&filterOrigin=jdp-text`;
      default:
        return;
    }
  }

  function locationFilterableLink(job) {
    const jobStateSlug = handleHref("stateRegion", job);

    if (job?.location == job?.stateRegion) {
      return <Link href={jobStateSlug}>{job?.location}</Link>;
    }

    return (
      <>
        {job?.location}, <Link href={jobStateSlug}>{job?.stateRegion}</Link>
      </>
    );
  }

  return (
    <JobDetailWrapper>
      <JobDetailContainer>
        <SingleJobDescription job={job} handleHref={handleHref} />
      </JobDetailContainer>
      {isDesktop && (
        <JobHighlightContainer>
          <SingleJobHighlightWithLogo
            job={job}
            locationFilter={locationFilterableLink}
            handleHref={handleHref}
          />
        </JobHighlightContainer>
      )}
    </JobDetailWrapper>
  );
}
